/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Edit page modal to be rendered on click of Add New store button inside store page of dedicated delivery.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { MRT_Row } from "material-react-table";
import { useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../../app/api/axiosError";
import { editExistingStore } from "../../../../app/features/dedicatedDelivery/storeSlice";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { EditStoreRequest } from "../../../../app/models/requestModels/StoreRequest";
import { DdStorePerformanceResponse } from "../../../../app/models/responseModels/DdStorePerformanceResponse";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import Loader from "../../../../components/common/loader/Loader";
import {
  ADDRESS_REGEX,
  ADDRESS_REGEX_INVALID_MSG,
  AIRPORT_CODE_REGEX,
  AIRPORT_CODE_REGEX_INVALID_MSG,
  CITY_REGEX,
  CITY_REGEX_INVALID_MSG,
  CLIENT_STORE_ID_REGEX,
  CLIENT_STORE_ID_REGEX_INVALID_MSG,
  ZIPCODE_REGEX,
  ZIPCODE_REGEX_INVALID_MSG,
} from "../../../../helpers/AppConstants";
import { STATES_LIST } from "../../../fdp/FdpConstants";
import StoreClaims from "./claims/StoreClaims";
import StoreLateCancel from "./lateCancel/StoreLateCancel";

// type to recieve parameter for this component to initialize
interface EditStoreModalProps {
  row: MRT_Row<DdStorePerformanceResponse>;
  clientIdArg: number;
  clientNameArg: string;
  closeModal: CallableFunction;
  onCompleted: CallableFunction;
}

// helper function to add data for add new store addition
function EditStoreFormModel(
  oldStoreId: number,
  airportCode: string,
  clientStoreId: string,
  minimumPackageCount:number,
  address: string,
  city: string,
  state: string,
  zipcode: string,

  latitude: number,
  longitude: number,
  isLongLatEnable: boolean,

  addressOne: string,
  cityOne: string,
  stateOne: string,
  zipcodeOne: string,
  altLatitudeOne: number,
  altLongitudeOne: number,
  isLongLatOneEnable: boolean,

  addressTwo: string,
  cityTwo: string,
  stateTwo: string,
  zipcodeTwo: string,

  altLatitudeTwo: number,
  altLongitudeTwo: number,
  isLongLatTwoEnable: boolean,

  fdps: number[],
  clientId: number,
  storeId: string,
  updatedBy: string,
  grossReceiptTax: number,

  receivingCutoffTime: string
): EditStoreRequest {
  return {
    oldStoreId: oldStoreId,
    clientId: clientId,
    airportCode: airportCode,
    clientStoreId: clientStoreId,
    minimumPackageCount:minimumPackageCount,
    address: address,
    city: city,
    state: state,
    zipcode: zipcode,

    latitude: latitude,
    longitude: longitude,
    isLongLatEnable: isLongLatEnable,

    addressOne: addressOne,
    cityOne: cityOne,
    stateOne: stateOne,
    zipcodeOne: zipcodeOne,

    altLatitudeOne: altLatitudeOne,
    altLongitudeOne: altLongitudeOne,
    isLongLatOneEnable: isLongLatOneEnable,

    addressTwo: addressTwo,
    cityTwo: cityTwo,
    stateTwo: stateTwo,
    zipcodeTwo: zipcodeTwo,

    altLatitudeTwo: altLatitudeTwo,
    altLongitudeTwo: altLongitudeTwo,
    isLongLatTwoEnable: isLongLatTwoEnable,

    fdps: fdps.map((e) => ({ id: e })),
    storeId: storeId,
    updatedBy: updatedBy,
    grossReceiptTax: grossReceiptTax,

    receivingCutoffTime: receivingCutoffTime,
  };
}

// Add new store modal component defination
const EditStoreModal = ({
  row,
  clientIdArg,
  closeModal,
  clientNameArg,
  onCompleted,
}: EditStoreModalProps) => {
  let cutOff: Date;
  if (row.original.receivingCutoffTime) {
    const hours = +row.original.receivingCutoffTime.substring(0, 2);
    const minutes = +row.original.receivingCutoffTime.substring(2, 4);
    cutOff = new Date();
    cutOff.setHours(hours);
    cutOff.setMinutes(minutes);
  }

  // data to keep in store
  const dispatch = useAppDispatch();
  const { isFdpLoading, fdpList } = useSelector(
    (state: RootState) => state.fdp
  );

  const { authResponse } = useSelector((state: RootState) => state.login);

  const [oldStoreId] = useState<number>(row.original.id);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isFieldsReadOnly, setIsFieldsReadOnly] = useState<boolean>(true);

  // import store data from "Redux-Store"
  const { storeData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryStore
  );

  const initialValues: any = {
    clientId: clientIdArg,
    airportCode: row.original.storeId.substring(0, 3),
    clientStoreId: row.original.storeId.substring(4),
    minimumPackageCount:row.original.minimumPackageCount,


    address: row.original.location.split("#")[0] ?? "",
    city: row.original.location.split("#")[1] ?? "",
    state: row.original.location.split("#")[2] ?? "",
    zipcode: row.original.location.split("#")[3] ?? "",
    longitude: row.original?.longitude,
    latitude: row.original?.latitude,
    isLongLatEnable: false,

    addressOne: row.original.altLocationOne?.split("#")[0] ?? "",
    cityOne: row.original.altLocationOne?.split("#")[1] ?? "",
    stateOne: row.original.altLocationOne?.split("#")[2] ?? "",
    zipcodeOne: row.original.altLocationOne?.split("#")[3] ?? "",
    altLongitudeOne: row.original?.altLongitudeOne,
    altLatitudeOne: row.original?.altLatitudeOne,
    isLongLatOneEnable: false,
    isLongLatTwoEnable: false,

    addressTwo: row.original.altLocationTwo?.split("#")[0] ?? "",
    cityTwo: row.original.altLocationTwo?.split("#")[1] ?? "",
    stateTwo: row.original.altLocationTwo?.split("#")[2] ?? "",
    zipcodeTwo: row.original.altLocationTwo?.split("#")[3] ?? "",
    altLongitudeTwo: row.original?.altLongitudeTwo,
    altLatitudeTwo: row.original?.altLatitudeTwo,

    fdps: row.original.fdps.map((e) => e.id),
    grossReceiptTax: row.original.grossReceiptTax,

    receivingCutoffTime: row.original.receivingCutoffTime,
  };

  const handleClearAddressOne = (
    formikProps: FormikProps<EditStoreRequest>
  ) => {
    formikProps.setFieldValue("addressOne", "");
    formikProps.setFieldValue("cityOne", "");
    formikProps.setFieldValue("stateOne", "");
    formikProps.setFieldValue("zipcodeOne", "");
  };

  const handleClearAddressTwo = (
    formikProps: FormikProps<EditStoreRequest>
  ) => {
    formikProps.setFieldValue("addressTwo", "");
    formikProps.setFieldValue("cityTwo", "");
    formikProps.setFieldValue("stateTwo", "");
    formikProps.setFieldValue("zipcodeTwo", "");
  };

  // validation rule while adding new store into the database
  const validationSchema = Yup.object().shape({
    clientId: Yup.number().required("Client Id is Required"),
    airportCode: Yup.string()
      .required("Nearest Airport Code is Required")
      .matches(AIRPORT_CODE_REGEX, AIRPORT_CODE_REGEX_INVALID_MSG),
    clientStoreId: Yup.string()
      .required("Client Store Id is Required")
      .matches(CLIENT_STORE_ID_REGEX, CLIENT_STORE_ID_REGEX_INVALID_MSG)
      .test(
        "checkClientStoreIdExistsWithClientAlready",
        "Client Store Id already exists for this client",
        /* async (value) => {
          if (row.original.clientStoreId !== value) {
            const response =
              await agent.DedicatedDeliveryAgent.checkIfStoreAlreadyExists({
                clientId: clientIdArg,
                clientStoreId: value,
              });
            return !response.data;
          } else {
            return true;
          }
        } */
        (value) => {
          if (row.original.clientStoreId !== value) {
            const find = storeData.find((e) => e.clientStoreId === value);
            return find === undefined;
          } else {
            return true;
          }
        }
      ),
    address: Yup.string()
      .required("Address is Required")
      .matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    city: Yup.string()
      .required("City is Required")
      .matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    state: Yup.string().required("State is Required"),
    zipcode: Yup.string()
      .required("Zipcode is Required")
      .matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    addressOne: Yup.string().matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    cityOne: Yup.string().matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    stateOne: Yup.string(),
    zipcodeOne: Yup.string().matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    addressTwo: Yup.string().matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    cityTwo: Yup.string().matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    stateTwo: Yup.string(),
    zipcodeTwo: Yup.string().matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    fdps: Yup.array().min(1, "Pick at least one fdp").of(Yup.number()),
    receivingCutoffTime: Yup.string(),
  });

  //on form submit make server call to persist the store
  const handleSubmit = async (values: any) => {
    try {
      setIsSubmitting(true);
      const editStoreDetails = EditStoreFormModel(
        oldStoreId,
        values.airportCode,
        values.clientStoreId,
        values.minimumPackageCount,
        values.address,
        values.city,
        values.state,
        values.zipcode,

        values.latitude,
        values.longitude,
        values.isLongLatEnable,

        values.addressOne,
        values.cityOne,
        values.stateOne,
        values.zipcodeOne,
        values.altLatitudeOne,
        values.altLongitudeOne,
        values.isLongLatOneEnable,

        values.addressTwo,
        values.cityTwo,
        values.stateTwo,
        values.zipcodeTwo,

        values.altLatitudeTwo,
        values.altLongitudeTwo,
        values.isLongLatTwoEnable,

        values.fdps,
        values.clientId,
        values.airportCode.toUpperCase() +
          clientNameArg.substring(0, 1) +
          values.clientStoreId,
        authResponse?.userSettings?.userName ?? "",
        values.grossReceiptTax,
        values.receivingCutoffTime
      );
      // console.log(editStoreDetails);
      const response = await dispatch(
        editExistingStore(editStoreDetails)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
      } else {
        closeModal(false);
      }
      toast.success("Store has been updated successfully.");
    } catch (error) {
      const appError = getAxiosError(error);
      if (appError.code === "400") {
        toast.error(
          "Store " +
            values.clientStoreId +
            " is already exists with client " +
            values.clientId,
          {
            toastId: "addStoreException",
          }
        );
      } else {
        toast.error(appError.errorMessage, {
          toastId: "addStoreException",
        });
      }
    } finally {
      setIsSubmitting(false);
      onCompleted();
    }
    return true;
  };

  const handleCancel = (
    resetForm: FormikHelpers<EditStoreRequest>["resetForm"]
  ) => {
    // Reset the form to its initial values
    resetForm();
    // Set the fields back to read-only mode
    setIsFieldsReadOnly(true);
  };

  return (
    <>
      <Loader isLoading={isFdpLoading} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: EditStoreRequest, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {(formikProps) => (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Form>
              <Grid container justifyContent="right" columnGap={2}>
                {isFieldsReadOnly && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      setIsFieldsReadOnly(!isFieldsReadOnly);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {!isFieldsReadOnly && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={isSubmitting}
                      type="submit"
                      onClick={(e) => {
                        handleCancel(formikProps.resetForm);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      Submit
                    </Button>
                  </>
                )}
              </Grid>

              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    margin="normal"
                    id="clientId"
                    name="clientId"
                    label="Client Id"
                    size="small"
                    fullWidth
                    variant={"filled"}
                    required
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="airportCode"
                    name="airportCode"
                    label="Nearest Airport Code"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="clientStoreId"
                    name="clientStoreId"
                    label="Client Store Id"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="address"
                    label="Address"
                    name="address"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="city"
                    label="City"
                    name="city"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    select
                    margin="normal"
                    id="state"
                    label="State"
                    name="state"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                    }}
                    required
                  >
                    {STATES_LIST.map((state, index) => (
                      <MenuItem key={index} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="zipcode"
                    label="Zipcode"
                    name="zipcode"
                    size="small"
                    type="text"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid item xs={6} sm={6} md={6} lg={3}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isFieldsReadOnly}
                      checked={formikProps?.values?.isLongLatEnable}
                      onChange={(event) =>
                        formikProps.setFieldValue(
                          "isLongLatEnable",
                          event.target.checked
                        )
                      } // OnChange handler
                      size="small" // Optional, controls the size of the switch
                    />
                  }
                  label="Do you want to Adjust Latitude/Longitude" // Your label text here
                  labelPlacement="start"
                />
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="latitude"
                    label="Latitude"
                    name="latitude"
                    type="number"
                    size="small"
                    fullWidth
                    variant={
                      !isFieldsReadOnly && formikProps.values.isLongLatEnable
                        ? "outlined"
                        : "filled"
                    }
                    InputProps={{
                      readOnly: !formikProps.values.isLongLatEnable,
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormikTextField
                    margin="normal"
                    id="longitude"
                    label="Longitude"
                    name="longitude"
                    type="number"
                    size="small"
                    fullWidth
                    variant={
                      !isFieldsReadOnly && formikProps.values.isLongLatEnable
                        ? "outlined"
                        : "filled"
                    }
                    InputProps={{
                      readOnly: !formikProps.values.isLongLatEnable,
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Card>
                <CardHeader subheader="Alternate Address 1" />
                <CardContent>
                  <Grid
                    container
                    columnSpacing={2}
                    alignItems="center"
                    justifyItems="center"
                  >
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="addressOne"
                        label="Address"
                        name="addressOne"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="cityOne"
                        label="City"
                        name="cityOne"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={5} sm={5} md={5} lg={2}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="stateOne"
                        label="State"
                        name="stateOne"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      >
                        {STATES_LIST.map((state, index) => (
                          <MenuItem key={index} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="zipcodeOne"
                        label="Zipcode"
                        name="zipcodeOne"
                        size="small"
                        type="text"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormControlLabel
                        style={{ marginLeft: "3px" }}
                        control={
                          <Switch
                            disabled={isFieldsReadOnly}
                            checked={formikProps?.values?.isLongLatOneEnable}
                            onChange={(event) =>
                              formikProps.setFieldValue(
                                "isLongLatOneEnable",
                                event.target.checked
                              )
                            } // OnChange handler
                            size="small" // Optional, controls the size of the switch
                          />
                        }
                        label="Do you want to Adjust Latitude/Longitude?" // Your label text here
                        labelPlacement="start"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="altLongitudeOne"
                        label="Alternate Longitude One"
                        name="altLongitudeOne"
                        size="small"
                        type="number"
                        fullWidth
                        variant={
                          !isFieldsReadOnly &&
                          formikProps.values.isLongLatOneEnable
                            ? "outlined"
                            : "filled"
                        }
                        InputProps={{
                          readOnly: !formikProps.values.isLongLatOneEnable,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="altLatitudeOne"
                        label="Alternate Latitude One"
                        name="altLatitudeOne"
                        size="small"
                        type="number"
                        fullWidth
                        variant={
                          !isFieldsReadOnly &&
                          formikProps.values.isLongLatOneEnable
                            ? "outlined"
                            : "filled"
                        }
                        InputProps={{
                          readOnly: !formikProps.values.isLongLatOneEnable,
                        }}
                      />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                      <Button
                        disabled={isFieldsReadOnly}
                        onClick={() => {
                          handleClearAddressOne(formikProps);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card style={{ marginTop: "10px" }}>
                <CardHeader subheader="Alternate Address 2" />
                <CardContent>
                  <Grid
                    container
                    columnSpacing={2}
                    alignItems="center"
                    justifyItems="center"
                  >
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="addressTwo"
                        label="Address"
                        name="addressTwo"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="cityTwo"
                        label="City"
                        name="cityTwo"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={5} sm={5} md={5} lg={2}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="stateTwo"
                        label="State"
                        name="stateTwo"
                        size="small"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      >
                        {STATES_LIST.map((state, index) => (
                          <MenuItem key={index} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="zipcodeTwo"
                        label="Zipcode"
                        name="zipcodeTwo"
                        size="small"
                        type="text"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormControlLabel
                        style={{ marginLeft: "3px" }}
                        control={
                          <Switch
                            disabled={isFieldsReadOnly}
                            checked={formikProps?.values?.isLongLatTwoEnable}
                            onChange={(event) =>
                              formikProps.setFieldValue(
                                "isLongLatTwoEnable",
                                event.target.checked
                              )
                            } // OnChange handler
                            size="small" // Optional, controls the size of the switch
                          />
                        }
                        label="Do you want to Adjust Latitude/Longitude?" // Your label text here
                        labelPlacement="start"
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="altLongitudeTwo"
                        label="Alternate Longitude Two"
                        name="altLongitudeTwo"
                        size="small"
                        type="number"
                        fullWidth
                        variant={
                          !isFieldsReadOnly &&
                          formikProps.values.isLongLatTwoEnable
                            ? "outlined"
                            : "filled"
                        }
                        InputProps={{
                          readOnly: !formikProps.values.isLongLatTwoEnable,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={3}>
                      <FormikTextField
                        margin="normal"
                        id="altLatitudeTwo"
                        label="Alternate Latitude Two"
                        name="altLatitudeTwo"
                        size="small"
                        type="number"
                        fullWidth
                        variant={
                          !isFieldsReadOnly &&
                          formikProps.values.isLongLatTwoEnable
                            ? "outlined"
                            : "filled"
                        }
                        InputProps={{
                          readOnly: !formikProps.values.isLongLatTwoEnable,
                        }}
                      />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                      <Button
                        disabled={isFieldsReadOnly}
                        onClick={() => {
                          handleClearAddressTwo(formikProps);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormikTextField
                    select
                    margin="normal"
                    id="fdps"
                    label="Select Associated FDP(s)"
                    name="fdps"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                    SelectProps={{
                      MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                      multiple: true,
                    }}
                    required
                  >
                    {fdpList.length > 0 ? fdpList
                      .filter((e) => e.active)
                      .sort((a, b) =>
                        a.companyName.localeCompare(b.companyName)
                      )
                      .map((fdp, index) => (
                        <MenuItem key={index} value={fdp.id}>
                          {fdp.companyName}
                        </MenuItem>
                      )): []}
                  </FormikTextField>
                </Grid>
              </Grid>
              <Card style={{ marginTop: "10px" }}>
                <CardHeader subheader="Manifest" />
                <CardContent>
                  <Grid
                    container
                    columnGap={1}
                    justifyItems="center"
                    alignItems="baseline"
                    marginTop={1}
                  >
                    <Grid item>
                      <Typography component="h3" variant="subtitle1">
                        Choose Cut-off:
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Tooltip title="This cut-off referes to the CutOff time agreed on the particular store, this field will decide whether if manifest received before cutoff then it'll be same day delivery else next day delivery.">
                        <IconButton>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <DatePicker
                        disableDayPicker
                        format="HHmm"
                        className="green"
                        dateSeparator=" ~ "
                        value={cutOff}
                        name="receivingCutoffTime"
                        onChange={(dateObject: DateObject) => {
                          const formattedDate = dateObject.format("HHmm");
                          formikProps.setFieldValue(
                            "receivingCutoffTime",
                            formattedDate
                          );
                        }}
                        plugins={[
                          <TimePicker
                            position="bottom"
                            key={"cutoff-picker"}
                            hideSeconds
                          />,
                        ]}
                        disabled={isFieldsReadOnly}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card style={{ marginTop: "10px", marginBottom: "10px" }}>
                <CardHeader subheader="Finance" />
                <CardContent>
                  <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormikTextField
                        margin="normal"
                        id="grossReceiptTax"
                        label="Gross Receipt Tax"
                        name="grossReceiptTax"
                        size="small"
                        type="decimal"
                        fullWidth
                        variant={!isFieldsReadOnly ? "outlined" : "filled"}
                        inputProps={{
                          min: 0,
                          max: 100,
                        }}
                        InputProps={{
                          readOnly: isFieldsReadOnly,
                          // endAdornment: <span>%</span>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <FormikTextField
  margin="normal"
  id="minimumPackageCount"
  label="Minimum Package Count"
  name="minimumPackageCount"
  size="small"
  type="number"
  fullWidth
  variant={!isFieldsReadOnly ? "outlined" : "filled"}
  InputProps={{
    readOnly: isFieldsReadOnly, // Make it read-only when the field should not be edited
    inputProps: {
      min: 0,
      max: 99,
      maxLength: 2,
      onInput: (e: any) => {
        if (!isFieldsReadOnly) { // Allow this logic only if the field is not read-only
          // Remove any non-digit characters
          e.target.value = e.target.value.replace(/[^\d]/g, '');
          // Limit to 2 digits
          if (e.target.value.length > 2) {
            e.target.value = e.target.value.slice(0, 2);
          }
          // Ensure value is between 0 and 99
          const num = parseInt(e.target.value);
          if (num > 99) {
            e.target.value = "99";
          }
        }
      }
    }
  }}
  SelectProps={{
    MenuProps: {
      PaperProps: { style: { maxHeight: 150 } },
    },
  }}
/>

</Grid>
                  </Grid>
                  <Card style={{ marginTop: "10px", marginBottom: "10px" }}>
                    <CardContent>
                      <Typography variant="h4" component="h1" marginBottom={1}>
                        Claims:
                      </Typography>
                      <StoreClaims metaData={row.original} />
                    </CardContent>
                  </Card>
                  <Card style={{ marginTop: "10px", marginBottom: "10px" }}>
                    <CardContent>
                      <Typography variant="h4" component="h1" marginBottom={1}>
                        Late Cancel:
                      </Typography>
                      <StoreLateCancel metaData={row.original} />
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </Form>
          </Box>
        )}
      </Formik>
    </>
  );
};

export default EditStoreModal;
